import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { RouteError } from "@/components/RouteError";
import { AssetNotFound } from "@/features/assets/components/AssetScreen";
import { DraftEditor } from "@/features/assets/components/DraftEditor";
import { titleOf } from "@/features/assets/media";
import { assetQuery } from "@/features/assets/queries";
import { isNotFound } from "@/lib/api";

/**
 * Which draft of a picture, video, or recording is open, by its version id; without one, the
 * asset's newest draft before its first publish. A basemap's drafts open in the basemap editor, at
 * `/assets/id/$assetId/drafts/$versionId`.
 */
export interface EditSearch {
  draft?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const Route = createFileRoute("/_authenticated/assets/id/$assetId/edit")({
  staticData: { access: "account" },
  validateSearch: (search: Record<string, unknown>): EditSearch => {
    const draft = String(search.draft ?? "");
    return UUID.test(draft) ? { draft } : {};
  },
  loaderDeps: ({ search }) => ({ draft: search.draft }),
  loader: ({ context: { queryClient, session }, params: { assetId }, deps: { draft } }) =>
    queryClient
      .ensureQueryData(assetQuery(assetId, session.user.id, draft))
      .then(({ asset }) => asset),
  head: ({ loaderData: asset }) => ({
    meta: [{ title: asset ? `Edit ${titleOf(asset)} · Pax.co` : "Edit asset · Pax.co" }],
  }),
  errorComponent: props =>
    isNotFound(props.error) ? <AssetNotFound /> : <RouteError {...props} />,
  pendingComponent: EditorPending,
  component: EditorPage,
});

function EditorPage() {
  const { assetId } = Route.useParams();
  const search = Route.useSearch();
  const { session } = Route.useRouteContext();
  const { data: asset } = useSuspenseQuery(assetQuery(assetId, session.user.id, search.draft));
  if (asset.kind === "basemap") return <AssetNotFound />;
  return <DraftEditor key={asset.versionId} asset={asset} />;
}

function EditorPending() {
  return (
    <div
      aria-busy
      className="app-reading-width grid gap-8 pt-20 lg:grid-cols-[minmax(0,1fr)_26rem]"
    >
      <div className="aspect-[4/3] animate-pulse rounded-2xl bg-foreground/5" />
      <div className="flex flex-col gap-4">
        <div className="h-10 animate-pulse rounded-xl bg-foreground/5" />
        <div className="h-28 animate-pulse rounded-xl bg-foreground/5" />
        <div className="h-10 animate-pulse rounded-xl bg-foreground/5" />
      </div>
    </div>
  );
}
